import type {
  PublicDispatchTrackingStage,
  PublicDispatchTrackingView,
} from "@/lib/api/dispatchTypes";

/**
 * The customer's delivery tracking page, the pure half (driver backend B4 ·
 * P16, plan D-27 / D-29).
 *
 * Nothing here fetches. It decides when the page reads the public tracking
 * answer again (and when it stops), what the timeline says, and turns the
 * answer's points into map geometry. The answer itself carries no customer
 * name, phone or address — only the destination pin — and this module adds
 * nothing to it.
 */

/** How often an open page re-reads while the order is still moving. */
export const TRACKING_POLL_MS = 15_000;
/** The longest wait between reads after failures. */
export const TRACKING_BACKOFF_MAX_MS = 120_000;
/** The first wait after a 429 (doubles per consecutive 429). */
export const TRACKING_RATE_LIMIT_BASE_MS = 30_000;

/** The token shape the backend mints: 16 random bytes, base64url, 22 chars. */
const TOKEN_SHAPE = /^[A-Za-z0-9_-]{22}$/;

/** A token that cannot be one of ours is not-found without asking the server. */
export function isTrackingTokenShape(token: string | undefined): boolean {
  return typeof token === "string" && TOKEN_SHAPE.test(token);
}

const STAGES = new Set(["PREPARING", "ON_THE_WAY", "ARRIVED", "DELIVERED", "NOT_DELIVERED"]);

/**
 * A 200 that is not the tracking answer (`{}`, a proxy's error JSON) must not
 * reach the renderer (P16 rework L5): the page treats it as a failed read.
 */
export function isTrackingView(value: unknown): value is PublicDispatchTrackingView {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  const isObject = (x: unknown) => Boolean(x) && typeof x === "object";
  return (
    typeof v.stage === "string" &&
    STAGES.has(v.stage) &&
    typeof v.orderNumber === "number" &&
    isObject(v.restaurant) &&
    typeof (v.restaurant as Record<string, unknown>).name === "string" &&
    isObject(v.timeline)
  );
}

/** Stages after which nothing on the page can change any more. */
export function isEndedStage(stage: PublicDispatchTrackingStage): boolean {
  return stage === "DELIVERED" || stage === "NOT_DELIVERED";
}

/** What one read came back as. */
export type TrackingReadOutcome =
  | { kind: "ok"; stage: PublicDispatchTrackingStage }
  /** 404: unknown, malformed or expired — one and the same answer. */
  | { kind: "notFound" }
  /** 429: this browser (or its network) read too often. */
  | { kind: "rateLimited" }
  /** Network failure or a 5xx. */
  | { kind: "failed" };

/** Whether to read again, and after how long. */
export type TrackingNextRead = { stop: true } | { stop: false; delayMs: number };

/**
 * The next read after `outcome`, where `failures` counts the consecutive
 * failed reads INCLUDING this one (0 after a success).
 *
 * - success: every 15 s while the order moves; stop once DELIVERED or
 *   NOT_DELIVERED (nothing more will change; the link expires on its own);
 * - 404: stop — the link is unknown or over, and asking again cannot change it;
 * - 429: back off 30 s, 60 s, 120 s … (cap 2 min);
 * - network / 5xx: 15 s, 30 s, 60 s … (cap 2 min).
 */
export function nextTrackingRead(
  outcome: TrackingReadOutcome,
  failures: number,
): TrackingNextRead {
  const step = Math.max(0, failures - 1);
  switch (outcome.kind) {
    case "ok":
      return isEndedStage(outcome.stage)
        ? { stop: true }
        : { stop: false, delayMs: TRACKING_POLL_MS };
    case "notFound":
      return { stop: true };
    case "rateLimited":
      return {
        stop: false,
        delayMs: Math.min(TRACKING_RATE_LIMIT_BASE_MS * 2 ** step, TRACKING_BACKOFF_MAX_MS),
      };
    case "failed":
      return {
        stop: false,
        delayMs: Math.min(TRACKING_POLL_MS * 2 ** step, TRACKING_BACKOFF_MAX_MS),
      };
  }
}

/** One row of the page's timeline. */
export type TimelineStep = {
  key: "preparing" | "onTheWay" | "arrived" | "delivered";
  state: "done" | "current" | "upcoming";
  /** ISO time the step happened, when known. */
  at: string | null;
};

const ORDER: TimelineStep["key"][] = ["preparing", "onTheWay", "arrived", "delivered"];

const CURRENT: Record<Exclude<PublicDispatchTrackingStage, "NOT_DELIVERED">, TimelineStep["key"]> = {
  PREPARING: "preparing",
  ON_THE_WAY: "onTheWay",
  ARRIVED: "arrived",
  DELIVERED: "delivered",
};

/**
 * The four steps with the current one marked. NOT_DELIVERED has no step of
 * its own: the steps reached stay done and the page says it did not complete.
 */
export function timelineSteps(view: PublicDispatchTrackingView): TimelineStep[] {
  const at: Record<TimelineStep["key"], string | null> = {
    preparing: view.timeline.acceptedAt,
    onTheWay: view.timeline.pickedUpAt,
    arrived: view.timeline.arrivedAt,
    delivered: view.timeline.deliveredAt,
  };
  if (view.stage === "NOT_DELIVERED") {
    return ORDER.map((key) => ({ key, state: at[key] ? "done" : "upcoming", at: at[key] }));
  }
  const current = ORDER.indexOf(CURRENT[view.stage]);
  return ORDER.map((key, index) => ({
    key,
    state:
      index < current
        ? "done"
        : index === current
          ? view.stage === "DELIVERED"
            ? "done"
            : "current"
          : "upcoming",
    at: at[key],
  }));
}

export type LatLngTuple = [number, number];

function validPoint(lat: unknown, lng: unknown): lat is number {
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
  );
}

/** What the map draws: each point only when the answer carries it. */
export type TrackingMapPoints = {
  restaurant: LatLngTuple | null;
  destination: LatLngTuple | null;
  driver: LatLngTuple | null;
  route: LatLngTuple[];
};

export function trackingMapPoints(view: PublicDispatchTrackingView): TrackingMapPoints {
  const { restaurant, destination, driverPosition, route } = view;
  return {
    restaurant: validPoint(restaurant.lat, restaurant.lng)
      ? [restaurant.lat as number, restaurant.lng as number]
      : null,
    destination:
      destination && validPoint(destination.lat, destination.lng)
        ? [destination.lat, destination.lng]
        : null,
    driver:
      driverPosition && validPoint(driverPosition.lat, driverPosition.lng)
        ? [driverPosition.lat, driverPosition.lng]
        : null,
    route: route?.polyline ? decodePolyline(route.polyline) : [],
  };
}

export function hasMapPoints(points: TrackingMapPoints): boolean {
  return Boolean(points.restaurant || points.destination || points.driver);
}

/**
 * Google's encoded polyline (precision 5) → points. Malformed input gives
 * what was decoded before the fault (or nothing) — never a throw on a public
 * page.
 */
export function decodePolyline(encoded: string): LatLngTuple[] {
  const points: LatLngTuple[] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  const next = (): number | null => {
    let result = 0;
    let shift = 0;
    for (;;) {
      if (index >= encoded.length) return null;
      const byte = encoded.charCodeAt(index++) - 63;
      if (byte < 0 || byte > 63) return null;
      result |= (byte & 0x1f) << shift;
      shift += 5;
      if (byte < 0x20) break;
      if (shift > 30) return null;
    }
    return result & 1 ? ~(result >> 1) : result >> 1;
  };
  while (index < encoded.length) {
    const dLat = next();
    const dLng = next();
    if (dLat === null || dLng === null) break;
    lat += dLat;
    lng += dLng;
    const point: LatLngTuple = [lat / 1e5, lng / 1e5];
    if (!validPoint(point[0], point[1])) break;
    points.push(point);
  }
  return points;
}

/** `tel:` for the driver's call button: digits and a leading `+` only. */
export function telHref(phone: string): string | null {
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 6) return null;
  return `tel:${trimmed.startsWith("+") ? "+" : ""}${digits}`;
}
